import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type AppNotification = {
  id: string;
  type: string;
  title: string;
  body: string | null;
  url: string | null;
  read_at: string | null;
  created_at: string;
  actor_id: string | null;
  actor_name?: string | null;
};

type TFn = (key: string, vars?: Record<string, string | number>) => string;

/**
 * Localized title for a notification. System types (e.g. new-message fan-out)
 * are rendered in the viewer's language; anything else falls back to the
 * stored title.
 */
export function notificationTitle(n: AppNotification, t: TFn): string {
  if (n.type === "message") {
    return t("messaging.notifMessage", { name: n.actor_name ?? t("messaging.someone") });
  }
  return n.title;
}

/** Live list of the signed-in member's notifications. */
export function useNotifications(limit = 30) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id;

  const query = useQuery({
    queryKey: ["notifications", userId],
    enabled: Boolean(userId),
    queryFn: async (): Promise<AppNotification[]> => {
      const { data, error } = await supabase
        .from("notifications")
        .select("id,type,title,body,url,read_at,created_at,actor_id")
        .eq("user_id", userId!)
        .order("created_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      const rows = (data ?? []) as AppNotification[];
      const actorIds = [...new Set(rows.map((r) => r.actor_id).filter(Boolean))] as string[];
      if (actorIds.length > 0) {
        const { data: actors } = await supabase
          .from("profiles")
          .select("id,display_name")
          .in("id", actorIds);
        const names = new Map(
          ((actors ?? []) as { id: string; display_name: string | null }[]).map((a) => [
            a.id,
            a.display_name,
          ]),
        );
        for (const row of rows) {
          row.actor_name = row.actor_id ? (names.get(row.actor_id) ?? null) : null;
        }
      }
      return rows;
    },
  });

  useEffect(() => {
    if (!userId) return;
    // A notification hook can be mounted more than once (header, messages,
    // notifications page), and React Strict Mode can remount effects before
    // the asynchronous channel cleanup finishes. Reusing the same topic in
    // that window returns an already-subscribed channel, which rejects `.on()`.
    const channelTopic = `notifications:${userId}:${crypto.randomUUID()}`;
    const channel = supabase
      .channel(channelTopic)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "notifications", filter: `user_id=eq.${userId}` },
        (payload) => {
          void queryClient.invalidateQueries({ queryKey: ["notifications", userId] });
          void queryClient.invalidateQueries({ queryKey: ["conversations", userId] });
          // Foreground alert: mirror the notification into the OS notification
          // centre when the member granted permission on this device.
          if (payload.eventType === "INSERT" && typeof window !== "undefined") {
            try {
              const row = payload.new as { title?: string; body?: string | null; url?: string | null };
              if ("Notification" in window && Notification.permission === "granted" && row.title) {
                const n = new Notification(row.title, {
                  body: row.body ?? "",
                  icon: "/brand/icon-192.png",
                  tag: "behot-notification",
                });
                n.onclick = () => {
                  window.focus();
                  if (row.url) window.location.assign(row.url);
                  n.close();
                };
              }
            } catch {
              /* notifications are best-effort */
            }
          }
        },
      )

      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [userId, queryClient]);

  const items = query.data ?? [];
  const unread = items.filter((n) => !n.read_at).length;

  async function markAllRead() {
    if (!userId) return;
    await supabase
      .from("notifications")
      .update({ read_at: new Date().toISOString() })
      .eq("user_id", userId)
      .is("read_at", null);
    await queryClient.invalidateQueries({ queryKey: ["notifications", userId] });
  }

  async function markRead(id: string) {
    await supabase.from("notifications").update({ read_at: new Date().toISOString() }).eq("id", id);
    await queryClient.invalidateQueries({ queryKey: ["notifications", userId] });
  }

  return { ...query, items, unread, markAllRead, markRead };
}
